import { CloudType, PolicyTargetType } from '../types/policies';
import { SelectableItemsResponse } from '../../CloudAlertManagement/services/api';

const RESOURCE_GROUPS_BY_TARGET_TYPE: Record<string, string[]> = {
  VIRTUAL_MACHINE: ['virtual-machine'],
  STORAGE_BUCKET: ['storage-bucket'],
  STORAGE_ACCOUNT: ['storage-bucket'],
  DATABASE: ['database'],
  EC2: ['ec2'],
  RDS: ['rds'],
  S3: ['s3'],
};

const RESOURCE_TARGET_TYPES = new Set(Object.keys(RESOURCE_GROUPS_BY_TARGET_TYPE));

const normalizeKey = (value: string) => value.trim().toLowerCase().replace(/_/g, '-').replace(/\s+/g, '-');

const normalizeTargetType = (value: PolicyTargetType | string) => String(value || '').trim().toUpperCase();

export const getAllowedResourceGroups = (
  policyTargetTypes: PolicyTargetType[] | string[] | undefined,
  cloudType?: CloudType | string,
): Set<string> | null => {
  const targetTypes = (policyTargetTypes || [])
    .map(normalizeTargetType)
    .filter(type => RESOURCE_TARGET_TYPES.has(type));

  if (targetTypes.length === 0) return null;

  const cloud = String(cloudType || '').toUpperCase();
  const allowed = new Set<string>();

  targetTypes.forEach(type => {
    RESOURCE_GROUPS_BY_TARGET_TYPE[type]?.forEach(group => allowed.add(group));
  });

  if (cloud === 'AWS') {
    ['VIRTUAL_MACHINE', 'STORAGE_BUCKET', 'STORAGE_ACCOUNT'].forEach(type => {
      if (targetTypes.includes(type)) RESOURCE_GROUPS_BY_TARGET_TYPE[type]?.forEach(group => allowed.delete(group));
    });
  } else if (cloud === 'GCP' || cloud === 'AZURE') {
    ['EC2', 'RDS', 'S3'].forEach(type => {
      if (targetTypes.includes(type)) RESOURCE_GROUPS_BY_TARGET_TYPE[type]?.forEach(group => allowed.delete(group));
    });
  }

  return allowed;
};

export const filterResourcesByPolicyTargetTypes = (
  resources: SelectableItemsResponse['resources'] | undefined,
  policyTargetTypes: PolicyTargetType[] | string[] | undefined,
  cloudType?: CloudType | string,
): SelectableItemsResponse['resources'] => {
  const source = resources || {};
  const allowedGroups = getAllowedResourceGroups(policyTargetTypes, cloudType);

  if (!allowedGroups) return source;

  return Object.entries(source).reduce<SelectableItemsResponse['resources']>((acc, [group, values]) => {
    if (allowedGroups.has(normalizeKey(group))) {
      acc[group] = values || [];
    }
    return acc;
  }, {});
};

export const getResourceOptionsForPolicy = (
  selectableItems: SelectableItemsResponse | null | undefined,
  policyTargetTypes: PolicyTargetType[] | string[] | undefined,
  cloudType?: CloudType | string,
): string[] => Object.values(
  filterResourcesByPolicyTargetTypes(selectableItems?.resources, policyTargetTypes, cloudType),
).flat();
